/**
 * Pure helpers for the league Schedule and Matches tabs.
 */
import { getScoreData, getServerSetScores } from '../../utils/matchDetailsUtils';
import { wallTimeToDate } from '../../utils/eventTime';

export const PAGE_SIZE = 50;

const TERMINAL = new Set(['completed', 'cancelled', 'forfeit']);
// Same list as the backend's standings (leagueStandingsCalculator SET_SPORTS)
const SET_SPORTS = new Set(['volleyball', 'badminton', 'table tennis', 'tennis', 'squash', 'pickleball']);

/** Results of these sports are entered set by set. */
export const usesSets = (sportType) =>
  SET_SPORTS.has(String(sportType || '').toLowerCase().replace(/[_-]+/g, ' ').trim());

/**
 * Has the game's start time passed? Its date and time are at the venue, so
 * they are read in the league's time zone (the viewer's without one).
 */
export const hasStarted = (game, now = new Date(), timeZone) => {
  const date = String(game?.game_date || '').slice(0, 10);
  if (!date) return false;
  const start = wallTimeToDate(date, String(game?.game_time || '00:00').slice(0, 5), timeZone);
  return Boolean(start) && start <= now;
};

/** Is a game or match over (completed, cancelled or rained out)? */
export const isFinished = (status) => TERMINAL.has(status);

/** A cancelled game whose reason is rain. */
export const isRainedOut = (item) =>
  /rain/i.test(item?.cancel_reason || '') || (item?.status === 'cancelled' && /rained out/i.test(item?.notes || ''));

/** 'participant1' | 'participant2' | null: the side that won a completed match. */
export const winnerSide = (match) => {
  if (match?.status !== 'completed' || match?.winner_id == null) return null;
  const winnerType = match.winner_type || 'team';
  const sideOf = (n) => Number(match[`participant_${n}_id`]) === Number(match.winner_id)
    && (match[`participant_${n}_type`] || 'team') === winnerType;
  if (sideOf(1)) return 'participant1';
  if (sideOf(2)) return 'participant2';
  return null;
};

/**
 * The final score of a completed match: sets won plus each set's points for
 * set sports ("1–0 · 21–15", "2–1 · 25–20, 20–25, 15–10"), else the totals
 * ("78–70"). null when no score was recorded.
 */
export const matchScoreLine = (match) => {
  const sd = getScoreData(match);
  const sets = getServerSetScores(match);
  const p1 = Number(sd.participant1);
  const p2 = Number(sd.participant2);
  const hasTotals = Number.isFinite(p1) && Number.isFinite(p2) && sd.participant1 != null && sd.participant2 != null;
  const setLine = sets.map((s) => `${s.p1}–${s.p2}`).join(', ');
  if (hasTotals && setLine) return `${p1}–${p2} · ${setLine}`;
  if (hasTotals) return `${p1}–${p2}`;
  return setLine || null;
};

/** Games grouped by night, keeping their order: [{ date, games }]. */
export const groupByNight = (games, dateOf = (g) => g.game_date || g.match_date) => {
  const nights = [];
  const index = new Map();
  for (const game of games) {
    const date = String(dateOf(game) || '').slice(0, 10) || 'TBD';
    if (!index.has(date)) {
      index.set(date, nights.length);
      nights.push({ date, games: [] });
    }
    nights[index.get(date)].games.push(game);
  }
  return nights;
};
